import { Button, Token, Typography, Wrapper } from '@screentone/core';

import EmbargoWarning from 'components/embargo-warning/EmbargoWarning';
import ExternalLink, { ExternalLinkContentType } from 'components/external-link/ExternalLink';
import { PreferredTimezone, PublicationSetting } from 'data/generated/graphql';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import styles from './TitleHeader.module.scss';
import { NewsletterScheduleDialog } from '../newsletter-schedule-dialog/NewsletterScheduleDialog';
import { NewsletterTestDialog } from '../newsletter-send-test-dialog/NewsletterSendTestDialog';

interface TitleHeaderProps {
  newsletterName?: string;
  readOnly: boolean;
  isScheduled?: boolean;
  newsletterId?: string;
  handleSendNowClick?: () => void;
  handleScheduleClick: (publishUtc: number) => void;
  handleSendTestClick: (emails: string) => void;
  publicationSetting?: PublicationSetting;
  latestEmbargoContent: AllessehContent | undefined;
  preferredTimezone?: PreferredTimezone;
}

export const TitleHeader = ({
  newsletterName,
  handleSendNowClick,
  readOnly,
  handleScheduleClick,
  isScheduled = false,
  handleSendTestClick,
  newsletterId,
  publicationSetting,
  latestEmbargoContent,
  preferredTimezone
}: TitleHeaderProps) => {
  const now = Date.now();
  const { embargo_datetime_utc: embargoDatetimeUtc = null } = latestEmbargoContent?.data.attributes ?? {};

  const latestEmbargoTimestamp = embargoDatetimeUtc ? new Date(embargoDatetimeUtc).getTime() : now;
  const latestEmbargoIsActive = latestEmbargoTimestamp > now;

  return (
    <Wrapper padding={{ top: 'md', bottom: 'md' }} className={styles.titleHeader}>
      <div className={styles.row} data-testid="newsletter-title">
        <Typography variant="header2" margin={{ bottom: 'none' }} data-testid="newsletter-title-header">
          {newsletterName}
        </Typography>
        {readOnly && <Token color={isScheduled ? 'blurple' : 'emerald'}>{isScheduled ? 'Schedule' : 'Sent'}</Token>}
      </div>
      <div className={styles.row}>
        {latestEmbargoContent && (
          <EmbargoWarning
            embargoInfo={latestEmbargoContent}
            preferredTimezone={preferredTimezone}
            dataTestId="newsletter-embargo-date-label"
          />
        )}
      </div>
      <div className={styles.row}>
        <ExternalLink contentId={newsletterId ?? ''} type={ExternalLinkContentType.COLLECTION} isPublished={readOnly} />
        {(!readOnly || isScheduled) && (
          <>
            <NewsletterTestDialog sendTestEmail={handleSendTestClick} />
            <Button
              secondary
              onClick={handleSendNowClick}
              data-testid="newsletter-send-now"
              disabled={latestEmbargoIsActive}
            >
              Send Now
            </Button>
            <NewsletterScheduleDialog
              reschedule={isScheduled}
              handleSchedule={handleScheduleClick}
              publicationSetting={publicationSetting}
              latestEmbargoTimestamp={latestEmbargoTimestamp}
              hasEmbargo={latestEmbargoIsActive}
            />
          </>
        )}
      </div>
    </Wrapper>
  );
};
